import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useWizardMode } from '@/contexts/WizardModeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileEdit, Landmark, TrendingUp, AlertCircle, Pencil, CalendarDays, RotateCcw, Clock, Activity, Flag, CheckCircle2, Users, LayoutList, SlidersHorizontal, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Check, X, Receipt, Target, Loader2, GitBranch, Save, Activity as ActivityIcon, GitMerge, ListChecks, BookOpen, Sliders, Database, Archive, Edit2, Search, Lock, BanIcon, Building2, LayoutGrid, XCircle, Info } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  PROJECT_ROLE_LABELS,
  PROJECT_ROLE_DISPLAY_ORDER,
  type ProjectRoleType,
  type ProjectRoleConfig,
  type CustomFieldType,
} from '@/types';
import { ProjectCreationSetup } from './project-creation-setup';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import { ProjectStatusLabels, type ProjectStatusLabelsHandle } from '@/components/settings/ProjectStatusLabels';
import { WriteOffReasonsSettings } from '@/components/settings/WriteOffReasonsSettings';
import { ProjectListSettings } from '@/components/settings/ProjectListSettings';
import { useSchedulingThresholds } from '@/hooks/use-scheduling-thresholds';
import { useBudgetHealthThresholds } from '@/hooks/use-budget-health-thresholds';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { useProjectContentSettings } from '@/hooks/use-project-content-settings';
import { useProjectCustomFields, MAX_FIELDS } from '@/hooks/use-project-custom-fields';
import { useBudgetPermissions } from '@/hooks/use-budget-permissions';
import type { BudgetRevisionSettings } from '@/types/resource-budget-settings.types';
import { useRevisionCategories } from '@/hooks/use-revision-categories';
import { RevisionReasonCategoryManager } from '@/components/blocks/budget/revision-reason-category-manager';
import { AddRevisionCategoryDialog } from '@/components/blocks/budget/add-revision-category-dialog';
import type { GlobalCategory } from '@/types/category.types';
import { CategoryFormDialog } from '@/components/settings/CategoryFormDialog';
import { CategoryProjectsSheet } from '@/components/settings/CategoryProjectsSheet';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { saveRevisionSettings, fetchRevisionSettings } from '@/lib/revision-settings-storage';
import { saveBudgetConfigurationAsync } from '@/lib/budget-configuration-storage';
import { fetchProjectRoleConfigs, saveProjectRoleConfigs, fetchGlobalCategories, createGlobalCategory, fetchCategoryStats, fetchCategoryTypes, updateGlobalCategory, deleteGlobalCategory, bulkReplaceCategoryNominalMappings, saveCategoryCostCentreMapping } from '@/lib/data-service';
import type { CategoryStats, CategoryType } from '@/types/category.types';
import type { CostCategoryType } from '@/types/budget.types';
import type { NominalCode } from '@/lib/cost-category-mapping-storage';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useBillingInvoiceSettings } from '@/hooks/use-billing-invoice-settings';
import { useWipThresholds } from '@/hooks/use-wip-thresholds';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// ============================================================================
// Accordion Section — matches the General Settings page card style
// ============================================================================

interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function AccordionSection({ icon, title, description, defaultOpen = true, open: controlledOpen, onOpenChange, children }: AccordionSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof v === 'function' ? v(open) : v;
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group"
        aria-expanded={open}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-6 py-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tab definitions
// ============================================================================

const PROJECT_TABS = [
  { value: 'project-creation', label: 'Project Creation', icon: FileEdit, description: 'Mandatory fields, project numbering, and default project settings.' },
  { value: 'budget-settings', label: 'Budget Settings', icon: Landmark, description: 'Budget type toggles and cost/sales category management.' },
  // Budget Governance hidden alongside revisions for first release — omit the tab
  // so the trigger no longer renders. TabsContent/state/handlers kept for re-enablement.
  // { value: 'budget-governance', label: 'Budget Governance', icon: GitBranch, description: 'Revision rules, cascade behaviour, and revision reason categories.' },
  { value: 'thresholds', label: 'Thresholds', icon: TrendingUp, description: 'Resource coverage and project health threshold configuration.' },
  { value: 'key-people', label: 'Key People', icon: Users, description: 'Configure which accountability roles appear on projects.' },
  { value: 'project-fields', label: 'Project Fields', icon: LayoutList, description: 'Configure content sections and custom attribute fields that appear on every project.' },
  { value: 'billing', label: 'Billing', icon: Receipt, description: 'Invoice numbering and billing configuration.' },
  { value: 'reference-data', label: 'Reference Data', icon: Database, description: 'Manage status labels used across all projects.' },
  { value: 'project-list', label: 'Project List', icon: ListChecks, description: 'Configure default columns and sorting for the project list.' },
] as const;

// ============================================================================

interface SettingsProjectProps {
  /** When set, locks the view to a single tab and hides the tab bar (used in wizard steps). */
  defaultTab?: string;
}

export default function SettingsProject({ defaultTab }: SettingsProjectProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isWizardMode, registerSave } = useWizardMode();

  const activeTab = defaultTab ?? searchParams.get('tab') ?? 'project-creation';
  const [isSaving, setIsSaving] = useState(false);
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // ===== SCHEDULING THRESHOLDS STATE =====
  const {
    thresholds,
    hasUnsavedChanges: hasThresholdChanges,
    isSaving: isSavingThresholds,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  } = useSchedulingThresholds();

  // ===== BUDGET HEALTH THRESHOLDS STATE =====
  const {
    thresholds: budgetThresholds,
    hasUnsavedChanges: hasBudgetThresholdChanges,
    isSaving: isSavingBudgetThresholds,
    updateThreshold: updateBudgetThreshold,
    saveThresholds: saveBudgetThresholds,
    resetToDefaults: resetBudgetThresholds,
  } = useBudgetHealthThresholds();



  // ===== BUDGET CATEGORIES STATE =====
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  // ===== KEY PEOPLE SETTINGS STATE =====
  // Defaults: all 8 roles enabled, only Project Manager required.
  const defaultRoleConfigs: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => ({
    role,
    enabled: true,
    required: role === 'project-manager',
    displayLabel: undefined,
  }));
  const [roleConfigs, setRoleConfigs] = useState<ProjectRoleConfig[]>(defaultRoleConfigs);
  const [savedRoleConfigs, setSavedRoleConfigs] = useState<ProjectRoleConfig[]>(defaultRoleConfigs);
  const [hasKeyPeopleChanges, setHasKeyPeopleChanges] = useState(false);
  const [isSavingKeyPeople, setIsSavingKeyPeople] = useState(false);

  // Load role configs from backend on mount (live mode only)
  useEffect(() => {
    const loadRoleConfigs = async () => {
      if (isDemoMode) return;
      try {
        const apiConfigs = await fetchProjectRoleConfigs();
        if (apiConfigs.length > 0) {
          // Merge API response with defaults so all roles are present
          const merged: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
            const fromApi = apiConfigs.find((c) => c.role === role);
            if (fromApi) {
              return {
                role: fromApi.role as ProjectRoleType,
                enabled: fromApi.enabled,
                required: fromApi.required,
                displayLabel: fromApi.displayLabel ?? undefined,
              };
            }
            return { role, enabled: true, required: role === 'project-manager', displayLabel: undefined };
          });
          setRoleConfigs(merged);
          setSavedRoleConfigs(merged);
        }
      } catch (err) {
        console.error('Failed to load role configs:', err);
      }
    };
    loadRoleConfigs();
  }, []);

  const updateRoleConfig = (role: ProjectRoleType, patch: Partial<ProjectRoleConfig>) => {
    setRoleConfigs((prev) =>
      prev.map((c) => {
        if (c.role !== role) return c;
        const updated = { ...c, ...patch };
        // Required can only be true when enabled
        if (!updated.enabled) updated.required = false;
        return updated;
      })
    );
    setHasKeyPeopleChanges(true);
  };

  const handleSaveKeyPeopleSettings = async () => {
    setIsSavingKeyPeople(true);
    try {
      if (!isDemoMode) {
        const configItems = roleConfigs.map((c) => ({
          role: c.role,
          enabled: c.enabled,
          required: c.required,
          displayLabel: c.displayLabel ?? null,
        }));
        await saveProjectRoleConfigs(configItems);
      }
      const changedRoles = roleConfigs
        .filter((c, i) => {
          const s = savedRoleConfigs[i];
          return c.enabled !== s.enabled || c.required !== s.required || c.displayLabel !== s.displayLabel;
        })
        .map(c => PROJECT_ROLE_LABELS[c.role]);
      appendAuditEntry({
        changedBy,
        category: 'Settings',
        action: 'Updated',
        entityName: 'Key People Role Configuration',
        newValue: changedRoles.length > 0 ? `Changed: ${changedRoles.join(', ')}` : 'No visible changes',
        detail: 'Updated via Project Settings > Key People',
      });
      setSavedRoleConfigs(roleConfigs);
      setHasKeyPeopleChanges(false);
    } catch (err) {
      console.error('Failed to save role configs:', err);
      throw err;
    } finally {
      setIsSavingKeyPeople(false);
    }
  };

  const handleDiscardKeyPeopleSettings = () => {
    setRoleConfigs(savedRoleConfigs);
    setHasKeyPeopleChanges(false);
  };

  // ===== PROJECT CONTENT FIELDS SETTINGS STATE =====
  const {
    settings: contentSettings,
    hasUnsavedChanges: hasContentChanges,
    isSaving: isSavingContent,
    updateSetting: updateContentSetting,
    saveSettings: saveContentSettings,
    resetToDefaults: resetContentSettings,
  } = useProjectContentSettings();

  // ===== PROJECT CUSTOM FIELDS (PROJECT ATTRIBUTES) STATE =====
  const {
    definitions: customFieldDefs,
    hasUnsavedChanges: hasCustomFieldChanges,
    isSaving: isSavingCustomFields,
    addDefinition,
    updateDefinition,
    removeDefinition,
    reorderDefinitions,
    saveDefinitions,
    discardChanges: discardCustomFieldChanges,
  } = useProjectCustomFields();

  // State for the "Add field" inline form
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<CustomFieldType>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState(''); // comma-separated

  // Inline options editing for existing select fields
  const [editingOptionsId, setEditingOptionsId] = useState<string | null>(null);
  const [editingOptionsValue, setEditingOptionsValue] = useState('');

  const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
    text: 'Text',
    number: 'Number',
    currency: 'Currency',
    date: 'Date',
    boolean: 'Yes / No',
    select: 'Dropdown',
  };

  const handleAddField = () => {
    if (!newFieldLabel.trim()) return;
    const options =
      newFieldType === 'select'
        ? newFieldOptions
            .split(',')
            .map((o) => o.trim())
            .filter(Boolean)
        : undefined;
    addDefinition({
      label: newFieldLabel.trim(),
      fieldType: newFieldType,
      required: newFieldRequired,
      enabled: true,
      placeholder: newFieldPlaceholder.trim() || undefined,
      options,
    });
    // Reset form
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldPlaceholder('');
    setNewFieldOptions('');
    setIsAddingField(false);
  };

  const handleCancelAddField = () => {
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldPlaceholder('');
    setNewFieldOptions('');
    setIsAddingField(false);
  };

  const handleMoveField = (id: string, direction: 'up' | 'down') => {
    const sorted = [...customFieldDefs].sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sorted.findIndex((d) => d.id === id);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sorted.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = sorted.map((d) => d.id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    reorderDefinitions(newOrder);
  };

  // ===== BUDGET SETTINGS STATE =====
  const {
    settings: budgetPermissions,
    updateBudgetMode,
    updatePermissions: updateBudgetPermissions,
    updateRevisionSettings,
    updateSettings: updateBudgetSettings,
  } = useBudgetPermissions();

  const [localBudgetMode, setLocalBudgetMode] = useState(budgetPermissions.budgetMode);
  const [localBudgetPermissions, setLocalBudgetPermissions] = useState(budgetPermissions.permissions);
  const [categoryAccordionOpen, setCategoryAccordionOpen] = useState(budgetPermissions.budgetMode === 'category');
  const [hasBudgetSettingsChanges, setHasBudgetSettingsChanges] = useState(false);

  const [localRevisionSettings, setLocalRevisionSettings] = useState<BudgetRevisionSettings>(
    budgetPermissions.revisionSettings
  );
  const [hasRevisionChanges, setHasRevisionChanges] = useState(false);
  const [isSavingRevision, setIsSavingRevision] = useState(false);
  const [showRevisionSuccess, setShowRevisionSuccess] = useState(false);

  // Derive from local state so the UI reacts immediately to changes before saving
  const isResourceBudgetEnabled = localBudgetMode !== 'disabled' && localBudgetPermissions.resourceBudgetEnabled;
  const isCostBudgetEnabled = localBudgetMode !== 'disabled' && localBudgetPermissions.costBudgetEnabled;
  const isSalesBudgetEnabled = localBudgetMode !== 'disabled' && localBudgetPermissions.salesBudgetEnabled;
  const isLocalCategoryMode = localBudgetMode === 'category';
  const isBudgetModeLocked = budgetPermissions.hasBudgetData && localBudgetMode !== 'disabled';

  const {
    allCategories,
    addCategory,
    toggleCategory,
    reorderCategories,
    deleteCategory,
  } = useRevisionCategories();

  // ===== BILLING INVOICE SETTINGS STATE (Feature 9 — Invoice Numbering) =====
  const {
    settings: billingInvoiceSettings,
    hasUnsavedChanges: hasBillingInvoiceChanges,
    isSaving: isSavingBillingInvoice,
    updateAutoNumbering,
    save: saveBillingInvoiceSettings,
    discard: discardBillingInvoiceSettings,
  } = useBillingInvoiceSettings();

  // ===== WIP THRESHOLD SETTINGS STATE (PS-11, PS-12, PS-13) =====
  const {
    thresholds: wipThresholds,
    hasUnsavedChanges: hasWipThresholdChanges,
    isSaving: isSavingWipThresholds,
    updateDaysThreshold,
    updateAmountWarning,
    updateAmountAlert,
    save: saveWipThresholds,
    discard: discardWipThresholds,
  } = useWipThresholds();

  // ===== PROJECT CREATION SETTINGS STATE =====
  const {
    hasUnsavedChanges: hasProjectCreationChanges,
    saveChanges: saveProjectCreationChanges,
    resetChanges: resetProjectCreationChanges,
  } = useFieldConfig();
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);

  // Cascade target category state
  const [labourCategories, setLabourCategories] = useState<GlobalCategory[]>([]);
  const [isLoadingLabourCategories, setIsLoadingLabourCategories] = useState(false);
  const [showTargetConfirmDialog, setShowTargetConfirmDialog] = useState(false);
  const [pendingTargetCategoryId, setPendingTargetCategoryId] = useState<number | null>(null);
  const [showAddLabourDialog, setShowAddLabourDialog] = useState(false);
  const [categoryRefreshKey, setCategoryRefreshKey] = useState(0);

  // ===== REFERENCE DATA TAB STATE =====
  const FALLBACK_TYPE_LABELS: Record<string, string> = {
    labour: 'Labour', expenses: 'Expenses', subcontractors: 'Subcontractors',
    materials: 'Materials', equipment: 'Equipment', overhead: 'Overhead', contingency: 'Contingency',
  };
  const CATEGORY_TYPE_COLORS: Record<string, string> = {
    labour: 'bg-blue-100 text-blue-800', expenses: 'bg-warning-100 text-warning-800',
    subcontractors: 'bg-purple-100 text-purple-800', materials: 'bg-emerald-100 text-emerald-800',
    equipment: 'bg-orange-100 text-orange-800', overhead: 'bg-slate-100 text-slate-800',
    contingency: 'bg-rose-100 text-rose-800',
  };
  const [refDataCategories, setRefDataCategories] = useState<GlobalCategory[]>([]);
  const [refDataCategoryStats, setRefDataCategoryStats] = useState<CategoryStats | null>(null);
  const [refDataIsLoading, setRefDataIsLoading] = useState(false);
  const [refDataApiCategoryTypes, setRefDataApiCategoryTypes] = useState<CategoryType[]>([]);
  const [refDataSearchTerm, setRefDataSearchTerm] = useState('');
  const [refDataStatusFilter, setRefDataStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [refDataTypeFilter, setRefDataTypeFilter] = useState<CostCategoryType | 'all'>('all');
  const [refDataShowFormDialog, setRefDataShowFormDialog] = useState(false);
  const [refDataEditingCategory, setRefDataEditingCategory] = useState<GlobalCategory | null>(null);
  const [refDataShowDeleteDialog, setRefDataShowDeleteDialog] = useState(false);
  const [refDataSelectedCategory, setRefDataSelectedCategory] = useState<GlobalCategory | null>(null);
  const [refDataIsDeleting, setRefDataIsDeleting] = useState(false);
  const [refDataShowProjectsSheet, setRefDataShowProjectsSheet] = useState(false);
  const [refDataProjectsSheetCategory, setRefDataProjectsSheetCategory] = useState<GlobalCategory | null>(null);

  const refDataTypeLabels: Record<string, string> = refDataApiCategoryTypes.length > 0
    ? Object.fromEntries(refDataApiCategoryTypes.map(ct => [ct.code, ct.label]))
    : FALLBACK_TYPE_LABELS;
  const refDataTypeFilterOptions = refDataApiCategoryTypes.length > 0
    ? refDataApiCategoryTypes.map(ct => ({ value: ct.code, label: ct.label }))
    : Object.entries(FALLBACK_TYPE_LABELS).map(([value, label]) => ({ value, label }));

  const refDataFilteredCategories = refDataCategories.filter(cat => {
    const matchesSearch = cat.name.toLowerCase().includes(refDataSearchTerm.toLowerCase()) ||
      (cat.code?.toLowerCase().includes(refDataSearchTerm.toLowerCase()) ?? false);
    const matchesStatus = refDataStatusFilter === 'all' || cat.status === refDataStatusFilter;
    const matchesType = refDataTypeFilter === 'all' || cat.type === refDataTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const loadRefDataCategories = useCallback(async () => {
    setRefDataIsLoading(true);
    try {
      const [catsData, statsData] = await Promise.all([fetchGlobalCategories(), fetchCategoryStats()]);
      setRefDataCategories(catsData);
      setRefDataCategoryStats(statsData);
    } catch (err) {
      console.error('Failed to load reference data categories:', err);
    } finally {
      setRefDataIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRefDataCategories();
    fetchCategoryTypes().then(setRefDataApiCategoryTypes).catch(() => {});
  }, [loadRefDataCategories]);

  const handleRefDataSaveCategory = async (data: {
    name: string; code?: string; type: CostCategoryType; description?: string;
    isDefault: boolean; costCodes: string[]; salesCodes: string[];
    allCostCodes: NominalCode[]; allSalesCodes: NominalCode[];
    costCentres: { code: string; name: string }[];
  }) => {
    const saveNominalMappings = async (categoryId: number) => {
      if (isDemoMode) return;
      const costLookup = new Map(data.allCostCodes.map(c => [c.nominalCode, c.nominalName]));
      const salesLookup = new Map(data.allSalesCodes.map(c => [c.nominalCode, c.nominalName]));
      await Promise.all([
        bulkReplaceCategoryNominalMappings(categoryId, {
          direction: 'cost',
          mappings: data.costCodes.map(code => ({ nominalCode: code, nominalName: costLookup.get(code) || code })),
        }),
        bulkReplaceCategoryNominalMappings(categoryId, {
          direction: 'sales',
          mappings: data.salesCodes.map(code => ({ nominalCode: code, nominalName: salesLookup.get(code) || code })),
        }),
      ]);
    };
    const saveCostCentreMapping = async (categoryId: number) => {
      if (isDemoMode) return;
      await saveCategoryCostCentreMapping(categoryId, data.costCentres);
    };
    try {
      if (refDataEditingCategory) {
        await updateGlobalCategory(refDataEditingCategory.id, {
          id: refDataEditingCategory.id, name: data.name, code: data.code, type: data.type,
          description: data.description, status: refDataEditingCategory.status,
          isDefault: data.isDefault, usageCount: refDataEditingCategory.usageCount,
        });
        await saveNominalMappings(refDataEditingCategory.id);
        await saveCostCentreMapping(refDataEditingCategory.id);
        toast.success(`Category "${data.name}" updated successfully`);
      } else {
        const newCat = await createGlobalCategory({ name: data.name, code: data.code, type: data.type, description: data.description, isDefault: data.isDefault });
        if (newCat?.id) {
          await saveNominalMappings(newCat.id);
          await saveCostCentreMapping(newCat.id);
        }
        toast.success(`Category "${data.name}" created successfully`);
      }
      await loadRefDataCategories();
      setCategoryRefreshKey(k => k + 1); // keep budget governance cascade selector in sync
    } catch (err: any) {
      const msg = err?.message || err?.detail || 'Failed to save category';
      toast.error(msg.toLowerCase().includes('already exists') ? 'A category with this code already exists.' : msg);
      throw err;
    }
  };

  const handleRefDataDeleteCategory = (cat: GlobalCategory) => {
    if (cat.status === 'archived') return;
    if (localRevisionSettings.cascadeTargetCategoryId != null && localRevisionSettings.cascadeTargetCategoryId === cat.id) {
      toast.error('Cannot archive or delete the cascade target category. Remove it as the target first.');
      return;
    }
    setRefDataSelectedCategory(cat);
    setRefDataShowDeleteDialog(true);
  };

  const confirmRefDataDelete = async () => {
    if (!refDataSelectedCategory) return;
    setRefDataIsDeleting(true);
    try {
      if (refDataSelectedCategory.usageCount > 0) {
        await updateGlobalCategory(refDataSelectedCategory.id, {
          id: refDataSelectedCategory.id, name: refDataSelectedCategory.name,
          code: refDataSelectedCategory.code, type: refDataSelectedCategory.type,
          description: refDataSelectedCategory.description, status: 'archived',
          isDefault: refDataSelectedCategory.isDefault, usageCount: refDataSelectedCategory.usageCount,
        });
        toast.success(`Category "${refDataSelectedCategory.name}" archived successfully`);
      } else {
        await deleteGlobalCategory(refDataSelectedCategory.id);
        toast.success(`Category "${refDataSelectedCategory.name}" deleted successfully`);
      }
      await loadRefDataCategories();
      setCategoryRefreshKey(k => k + 1);
    } catch (err: any) {
      const msg = err?.message || err?.metadata?.apiError?.error || 'Failed to delete/archive category';
      toast.error(msg);
    } finally {
      setRefDataIsDeleting(false);
      setRefDataShowDeleteDialog(false);
      setRefDataSelectedCategory(null);
    }
  };

  const handleRefDataRestoreCategory = async (cat: GlobalCategory) => {
    try {
      await updateGlobalCategory(cat.id, {
        id: cat.id, name: cat.name, code: cat.code, type: cat.type,
        description: cat.description, status: 'active', isDefault: cat.isDefault, usageCount: cat.usageCount,
      });
      toast.success(`Category "${cat.name}" restored successfully`);
      await loadRefDataCategories();
      setCategoryRefreshKey(k => k + 1);
    } catch {
      toast.error('Failed to restore category');
    }
  };

  const loadLabourCategories = useCallback(async () => {
    setIsLoadingLabourCategories(true);
    try {
      const cats = await fetchGlobalCategories();
      const filtered = cats.filter(c => c.type === 'labour' && c.status === 'active');
      setLabourCategories(filtered);
      // Auto-select the first Labour category if none is currently selected
      if (filtered.length > 0) {
        setLocalRevisionSettings(prev => {
          if (!prev.cascadeTargetCategoryId) {
            setHasRevisionChanges(true);
            return { ...prev, cascadeTargetCategoryId: filtered[0].id };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Failed to load labour categories:', err);
    } finally {
      setIsLoadingLabourCategories(false);
    }
  }, []);

  useEffect(() => {
    loadLabourCategories();
  }, [loadLabourCategories, categoryRefreshKey]);

  const handleRevisionSettingChange = (key: keyof BudgetRevisionSettings, value: boolean) => {
    setLocalRevisionSettings(prev => ({ ...prev, [key]: value }));
    setHasRevisionChanges(true);
  };

  const handleSaveRevisionSettings = async () => {
    setIsSavingRevision(true);
    try {
      // Persist to API (throws on failure, localStorage fallback already written)
      await saveRevisionSettings(localRevisionSettings);

      // Re-fetch to verify persistence and sync cache/localStorage
      const refreshed = await fetchRevisionSettings();

      // Update hook state with verified data (without triggering another persist)
      updateBudgetSettings({ revisionSettings: refreshed });

      // Update local state to match what's actually persisted
      setLocalRevisionSettings(refreshed);

      const targetCatName = labourCategories.find(c => c.id === refreshed.cascadeTargetCategoryId)?.name ?? 'None';
      appendAuditEntry({
        changedBy,
        category: 'Settings',
        action: 'Updated',
        entityName: 'Resource Budget Revision Behaviour',
        newValue: `Auto-update Cost Labour: ${refreshed.autoUpdateCostLabour ? 'On' : 'Off'}, Auto-update Sales Labour: ${refreshed.autoUpdateSalesLabour ? 'On' : 'Off'}, Cascade Target: ${targetCatName}`,
        detail: 'Updated via Project Settings > Budget Settings > Revision Behaviour',
      });
      setHasRevisionChanges(false);
      setShowRevisionSuccess(true);
      setTimeout(() => setShowRevisionSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save revision settings:', error);
      throw error;
    } finally {
      setIsSavingRevision(false);
    }
  };

  const handleResetRevisionSettings = () => {
    setLocalRevisionSettings(budgetPermissions.revisionSettings);
    setHasRevisionChanges(false);
    toast.info('Changes reset');
  };

  // Keep localRevisionSettings in sync with hook state (e.g. after async fetch)
  // only when the user has no pending unsaved edits.
  useEffect(() => {
    if (!hasRevisionChanges) {
      setLocalRevisionSettings(budgetPermissions.revisionSettings);
    }
  }, [budgetPermissions.revisionSettings, hasRevisionChanges]);

  // ── Budget Settings handlers ──────────────────────────────────────────────
  const handleBudgetModeChange = (mode: typeof localBudgetMode) => {
    setLocalBudgetMode(mode);
    setHasBudgetSettingsChanges(true);
    if (mode === 'category') setCategoryAccordionOpen(true);
  };

  const handleBudgetPermissionChange = (key: keyof typeof localBudgetPermissions, value: boolean) => {
    setLocalBudgetPermissions(prev => ({ ...prev, [key]: value }));
    setHasBudgetSettingsChanges(true);
  };

  const saveBudgetSettings = async () => {
    await saveBudgetConfigurationAsync({
      budgetMode: localBudgetMode,
      costBudgetEnabled: localBudgetPermissions.costBudgetEnabled,
      salesBudgetEnabled: localBudgetPermissions.salesBudgetEnabled,
      resourceBudgetEnabled: localBudgetPermissions.resourceBudgetEnabled,
    });
    updateBudgetMode(localBudgetMode);
    updateBudgetPermissions(localBudgetPermissions);
    setHasBudgetSettingsChanges(false);
  };

  // Keep local state in sync with hook when no unsaved changes
  useEffect(() => {
    if (!hasBudgetSettingsChanges) {
      setLocalBudgetMode(budgetPermissions.budgetMode);
      setLocalBudgetPermissions(budgetPermissions.permissions);
    }
  }, [budgetPermissions.budgetMode, budgetPermissions.permissions, hasBudgetSettingsChanges]);

  const statusLabelsRef = useRef<ProjectStatusLabelsHandle>(null);
  const [hasStatusLabelChanges, setHasStatusLabelChanges] = useState(false);

  const hasAnyChanges =
    hasStatusLabelChanges ||
    hasProjectCreationChanges ||
    hasBudgetSettingsChanges ||
    hasThresholdChanges ||
    hasBudgetThresholdChanges ||
    hasRevisionChanges ||
    hasKeyPeopleChanges ||
    hasContentChanges ||
    hasCustomFieldChanges ||
    hasBillingInvoiceChanges ||
    hasWipThresholdChanges;

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const saves: Promise<unknown>[] = [];
      if (hasProjectCreationChanges) saves.push(saveProjectCreationChanges());
      if (hasBudgetSettingsChanges) saves.push(saveBudgetSettings());
      if (hasThresholdChanges) saves.push(saveThresholds());
      if (hasBudgetThresholdChanges) saves.push(saveBudgetThresholds());
      if (hasRevisionChanges) saves.push(handleSaveRevisionSettings());
      if (hasKeyPeopleChanges) saves.push(handleSaveKeyPeopleSettings());
      if (hasContentChanges) saves.push(saveContentSettings());
      if (hasCustomFieldChanges) saves.push(saveDefinitions());
      if (hasBillingInvoiceChanges) saves.push(saveBillingInvoiceSettings());
      if (hasWipThresholdChanges) saves.push(saveWipThresholds());
      if (hasStatusLabelChanges && statusLabelsRef.current) saves.push(statusLabelsRef.current.save());
      await Promise.all(saves);
      if (saves.length > 0 && !isWizardMode) {
        toast.success('All settings saved successfully.');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
      }
    } catch {
      toast.error('Some settings failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Register save function with wizard so Next button can trigger it.
  // A ref ensures the callback always invokes the latest handleSaveAll.
  const wizardSaveRef = useRef(handleSaveAll);
  wizardSaveRef.current = handleSaveAll;
  useState(() => { if (isWizardMode) registerSave(async () => { await wizardSaveRef.current(); }); });

  const content = (
    <div className="space-y-6 p-6">

            {/* Page header — hidden when embedded in wizard (step provides its own heading) */}
            {!defaultTab && (
              <div>
                <h1 className="text-xl font-semibold">
                  {PROJECT_TABS.find((t) => t.value === activeTab)?.label ?? 'Project Settings'}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {PROJECT_TABS.find((t) => t.value === activeTab)?.description}
                </p>
              </div>
            )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={defaultTab ? undefined : handleTabChange} className="space-y-6">
          {!defaultTab && (
            <TabsList className="w-full justify-start">
              {PROJECT_TABS.map(({ value, label, icon: Icon }) => (
                <TabsTrigger key={value} value={value} className="flex items-center gap-2 flex-none">
                  <Icon className="h-4 w-4" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          {/* Project Creation Tab */}
          <TabsContent value="project-creation" className="mt-0">
            <ProjectCreationSetup />
          </TabsContent>

          {/* Budget Settings Tab */}
          <TabsContent value="budget-settings" className="mt-0">
            <div className="space-y-4">

              {/* Budget Mode + Budget Categories — combined card */}
              <AccordionSection
                icon={<LayoutGrid className="h-5 w-5 text-primary" />}
                title="Budget Tracking"
                description="Choose how budgets are tracked across your organisation and which categories are available."
              >
                <div className="space-y-6">
                  {/* Locked warning */}
                  {isBudgetModeLocked && (
                    <Alert variant="default" className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
                      <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <AlertTitle className="text-amber-800 dark:text-amber-200">Budget Tracking Mode Locked</AlertTitle>
                      <AlertDescription className="text-amber-700 dark:text-amber-300">
                        The budget tracking mode cannot be changed because one or more projects already have budget data.
                        To change the mode, all existing budget data must first be removed. Please contact support if you need to reconfigure.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Budget Mode options */}
                  {(
                    [
                      {
                        value: 'disabled' as const,
                        title: 'Disabled',
                        description: 'Budget tracking is completely disabled. All budget tabs (Cost, Sales, Resource) will be hidden from all projects.',
                      },
                      {
                        value: 'category' as const,
                        title: 'Category-Based',
                        description: 'Budget entered against cost categories (e.g. Labour, Materials, Subcontractor). Select which budget types to track below.',
                      },
                      {
                        value: 'cost-centre' as const,
                        title: 'Cost Centre',
                        description: 'Budget entered against the cost centre hierarchy within each project. Select which budget types to track below.',
                      },
                    ] as const
                  ).map((option, index, arr) => (
                    <div key={option.value}>
                      <div
                        className={`flex items-center gap-4 ${isBudgetModeLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={() => !isBudgetModeLocked && handleBudgetModeChange(option.value)}
                      >
                        <input
                          type="radio"
                          name="budgetMode"
                          checked={localBudgetMode === option.value}
                          onChange={() => handleBudgetModeChange(option.value)}
                          disabled={isBudgetModeLocked}
                          className="h-4 w-4 shrink-0 border-neutral-300 text-primary focus:ring-primary disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Label className={`text-sm font-semibold ${isBudgetModeLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>{option.title}</Label>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{option.description}</p>
                        </div>
                      </div>
                      {index < arr.length - 1 && <Separator className="mt-6" />}
                    </div>
                  ))}

                  {/* Budget Categories — visible when not disabled */}
                  {localBudgetMode !== 'disabled' && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-sm font-semibold mb-3">Enable Budget Types</p>
                        {localBudgetMode === 'cost-centre' && (
                          <Alert className="mb-3">
                            <Info className="h-4 w-4" />
                            <AlertTitle>All budget types are always enabled in Cost Centre mode</AlertTitle>
                            <AlertDescription>
                              When using Cost Centre budgeting, all three budget types (Cost, Revenue, Resource) are always active. To control which budget types are available, switch to Category-Based mode.
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="grid grid-cols-3 gap-3">
                          {(
                            [
                              { key: 'costBudgetEnabled' as const, title: 'Cost', description: 'Plan and track project expenditure' },
                              { key: 'salesBudgetEnabled' as const, title: 'Revenue', description: 'Plan and track project income and sales' },
                              { key: 'resourceBudgetEnabled' as const, title: 'Resource', description: 'Plan and track hours and team allocation' },
                            ] as const
                          ).map((cat) => {
                            const isCostCentre = localBudgetMode === 'cost-centre';
                            return (
                              <div
                                key={cat.key}
                                className={`flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3 transition-colors ${
                                  isCostCentre
                                    ? 'pointer-events-none opacity-60'
                                    : 'cursor-pointer hover:bg-muted/30'
                                }`}
                                onClick={isCostCentre ? undefined : () => handleBudgetPermissionChange(cat.key, !localBudgetPermissions[cat.key])}
                              >
                                <div>
                                  <p className="text-sm font-medium">{cat.title}</p>
                                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                                </div>
                                <Checkbox
                                  id={cat.key}
                                  checked={localBudgetPermissions[cat.key]}
                                  disabled={isCostCentre}
                                  onCheckedChange={isCostCentre ? undefined : (v) => handleBudgetPermissionChange(cat.key, !!v)}
                                />
                              </div>
                            );
                          })}
                        </div>

                        {!localBudgetPermissions.costBudgetEnabled &&
                         !localBudgetPermissions.salesBudgetEnabled &&
                         !localBudgetPermissions.resourceBudgetEnabled &&
                         localBudgetMode !== 'cost-centre' && (
                          <Alert variant="destructive" className="mt-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>No Budget Tabs Enabled</AlertTitle>
                            <AlertDescription>
                              At least one budget tab should be visible. Projects will have no budget tabs until one is enabled.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </AccordionSection>

              {/* Global Budget Categories — Category mode only */}
              {localBudgetMode === 'category' && (
                <AccordionSection
                  icon={<Database className="h-5 w-5 text-primary" />}
                  title="Global Budget Categories"
                  description="Manage the cost categories available across all projects."
                  open={categoryAccordionOpen}
                  onOpenChange={setCategoryAccordionOpen}
                >
                  <div className="space-y-4">

                    {/* Filters + Add button row */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search by name or code…"
                          value={refDataSearchTerm}
                          onChange={e => setRefDataSearchTerm(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <Select value={refDataTypeFilter} onValueChange={v => setRefDataTypeFilter(v as CostCategoryType | 'all')}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {refDataTypeFilterOptions.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        {(['active', 'archived', 'all'] as const).map(s => (
                          <Button key={s} variant={refDataStatusFilter === s ? 'default' : 'outline'} size="sm"
                            onClick={() => setRefDataStatusFilter(s)}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </Button>
                        ))}
                      </div>
                      <Button size="sm" onClick={() => { setRefDataEditingCategory(null); setRefDataShowFormDialog(true); }}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Category
                      </Button>
                    </div>

                    {/* Categories list */}
                    <div className="space-y-2">
                      {refDataIsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : refDataFilteredCategories.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground text-sm">
                          No categories found matching your search.
                        </div>
                      ) : (
                        refDataFilteredCategories.map(cat => (
                          <div key={cat.id} className={`rounded-lg border border-border ${cat.status === 'archived' ? 'opacity-60' : ''}`}>
                            <div className="p-4 flex items-center gap-3 hover:bg-muted/50">
                              <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-6">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{cat.name}</span>
                                    <Badge className={`text-xs font-medium border-0 ${CATEGORY_TYPE_COLORS[cat.type] || 'bg-neutral-100 text-neutral-800'}`}>
                                      {refDataTypeLabels[cat.type] || cat.type}
                                    </Badge>
                                    {cat.isLabourTarget && (
                                      <Badge variant="outline" className="text-xs">Resource Budget Target</Badge>
                                    )}
                                  </div>
                                  {cat.code && <p className="text-xs text-muted-foreground mt-0.5">{cat.code}</p>}
                                </div>
                                <div className="col-span-3 text-sm">
                                  {cat.usageCount > 0 ? (
                                    <button className="text-primary hover:underline" onClick={() => { setRefDataProjectsSheetCategory(cat); setRefDataShowProjectsSheet(true); }}>
                                      {cat.usageCount} project{cat.usageCount !== 1 ? 's' : ''}
                                    </button>
                                  ) : (
                                    <span className="text-muted-foreground">0 projects</span>
                                  )}
                                </div>
                                <div className="col-span-2 flex items-center gap-1 flex-wrap">
                                  {cat.isDefault && <Badge variant="outline" className="text-xs">Default</Badge>}
                                  {cat.status === 'archived' && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                                  {localRevisionSettings.cascadeTargetCategoryId != null && localRevisionSettings.cascadeTargetCategoryId === cat.id && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                                            <Target className="h-3 w-3 mr-1" />Cascade Target
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent><p>This is the cascade target for Resource Budget revisions.</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <div className="col-span-1 flex justify-end gap-1">
                                  {cat.status !== 'archived' && (
                                    <Button variant="ghost" size="sm" onClick={() => { setRefDataEditingCategory(cat); setRefDataShowFormDialog(true); }}>
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {localRevisionSettings.cascadeTargetCategoryId != null && localRevisionSettings.cascadeTargetCategoryId === cat.id ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span><Button variant="ghost" size="sm" disabled><Lock className="h-4 w-4 text-muted-foreground" /></Button></span>
                                        </TooltipTrigger>
                                        <TooltipContent><p>Cannot archive — this is the cascade target category.</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : cat.status === 'archived' ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button variant="ghost" size="sm" onClick={() => handleRefDataRestoreCategory(cat)}>
                                            <RotateCcw className="h-4 w-4 text-success-600" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent><p>Restore to active</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : cat.usageCount > 0 ? (
                                    <Button variant="ghost" size="sm" onClick={() => handleRefDataDeleteCategory(cat)}>
                                      <Archive className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                  ) : (
                                    <Button variant="ghost" size="sm" onClick={() => handleRefDataDeleteCategory(cat)}>
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </AccordionSection>
              )}

            </div>
          </TabsContent>

          {/* ── Budget Governance Tab ────────────────────────────────────────── */}
          <TabsContent value="budget-governance" className="mt-0">
            <div className="space-y-4">

              {/* Revision Behaviour — combined card */}
              <AccordionSection
                icon={<GitBranch className="h-5 w-5 text-primary" />}
                title="Revision Behaviour"
                description="Configure revision notes requirements and how resource budget revisions cascade into linked budgets."
              >
                <div className="space-y-6">

                  {showRevisionSuccess && (
                    <Alert className="border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-950">
                      <AlertCircle className="h-4 w-4 text-success-600 dark:text-success-400" />
                      <AlertTitle className="text-success-800 dark:text-success-200">Settings Saved</AlertTitle>
                      <AlertDescription className="text-success-700 dark:text-success-300">
                        Budget revision settings have been updated successfully.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Two-column card grid for all five revision behaviour items */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* Row 1 left — Require Revision Notes on Cost Budget Save */}
                    {isCostBudgetEnabled && (
                      <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold">Require Revision Notes on Cost Budget Save</p>
                          <Checkbox
                            id="gov-require-notes-cost"
                            checked={localRevisionSettings.requireRevisionNotesOnCostSave ?? false}
                            onCheckedChange={(checked) => handleRevisionSettingChange('requireRevisionNotesOnCostSave', !!checked)}
                            className="mt-0.5 shrink-0"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          When enabled, a reason category must be selected before saving any cost budget change. Notes are always required when making a revision active.
                        </p>
                      </div>
                    )}

                    {/* Row 1 right — Require Revision Notes on Revenue Budget Save */}
                    {isSalesBudgetEnabled && (
                      <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold">Require Revision Notes on Revenue Budget Save</p>
                          <Checkbox
                            id="gov-require-notes-sales"
                            checked={localRevisionSettings.requireRevisionNotesOnSalesSave ?? false}
                            onCheckedChange={(checked) => handleRevisionSettingChange('requireRevisionNotesOnSalesSave', !!checked)}
                            className="mt-0.5 shrink-0"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          When enabled, a reason category must be selected before saving any revenue budget change. Notes are always required when making a revision active.
                        </p>
                      </div>
                    )}

                    {isResourceBudgetEnabled && (
                      <>
                        {/* Row 2 left — Require Revision Notes on Resource Budget Save */}
                        <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-semibold">Require Revision Notes on Resource Budget Save</p>
                            <Checkbox
                              id="gov-require-notes-resource"
                              checked={localRevisionSettings.requireRevisionNotes ?? false}
                              onCheckedChange={(checked) => handleRevisionSettingChange('requireRevisionNotes', !!checked)}
                              className="mt-0.5 shrink-0"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            When enabled, a reason category must be selected before saving any resource budget change. Notes are always required when making a revision active.
                          </p>
                        </div>

                        {/* Row 2 right — Auto-update Labour in Cost Budget */}
                        <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-semibold">Auto-update Labour in Cost Budget</p>
                            <Checkbox
                              id="gov-auto-update-cost-labour"
                              checked={localRevisionSettings.autoUpdateCostLabour}
                              onCheckedChange={(checked) => handleRevisionSettingChange('autoUpdateCostLabour', !!checked)}
                              className="mt-0.5 shrink-0"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            When a resource budget revision is approved, automatically update the corresponding labour rows in the cost budget.
                          </p>
                        </div>

                        {/* Row 3 left — Auto-update Labour in Revenue Budget */}
                        <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-semibold">Auto-update Labour in Revenue Budget</p>
                            <Checkbox
                              id="gov-auto-update-sales-labour"
                              checked={localRevisionSettings.autoUpdateSalesLabour}
                              onCheckedChange={(checked) => handleRevisionSettingChange('autoUpdateSalesLabour', !!checked)}
                              className="mt-0.5 shrink-0"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            When a resource budget revision is approved, automatically update the corresponding labour rows in the revenue budget.
                          </p>
                        </div>
                      </>
                    )}

                  </div>

                  {isResourceBudgetEnabled && isLocalCategoryMode && (localRevisionSettings.autoUpdateCostLabour || localRevisionSettings.autoUpdateSalesLabour) && <Separator />}

                      {/* Cascade Target Category — only shown in Category-Based mode when at least one auto-update is enabled */}
                      {isResourceBudgetEnabled && isLocalCategoryMode && (localRevisionSettings.autoUpdateCostLabour || localRevisionSettings.autoUpdateSalesLabour) && (
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Target className="h-4 w-4" />
                              Cascade Target Category
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Select which Labour category receives the cascaded budget values from the Resource Budget.
                              Only this category will be updated when hours are converted to monetary values.
                            </p>
                          </div>

                          {isLoadingLabourCategories ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading labour categories...
                            </div>
                          ) : labourCategories.length === 0 ? (
                            <div className="rounded-md border border-dashed p-4 space-y-3">
                              <p className="text-sm text-muted-foreground">
                                No active Labour categories found. Create one in Budget Settings, or use the button below.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowAddLabourDialog(true)}
                              >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Add New Labour Category
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Select
                                value={localRevisionSettings.cascadeTargetCategoryId?.toString() ?? ''}
                                onValueChange={(val) => {
                                  const numVal = val ? parseInt(val, 10) : null;
                                  setPendingTargetCategoryId(numVal);
                                  setShowTargetConfirmDialog(true);
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select a Labour category..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {labourCategories.map((cat) => (
                                    <SelectItem key={cat.id} value={cat.id.toString()}>
                                      <span className="flex items-center gap-2">
                                        {cat.name}
                                        {cat.code && <span className="text-xs text-muted-foreground">({cat.code})</span>}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>


                            </>
                          )}
                        </div>
                      )}

                </div>
              </AccordionSection>

              {/* Revision Reason Categories */}
              {isResourceBudgetEnabled && (
                <AccordionSection
                  icon={<Users className="h-5 w-5 text-primary" />}
                  title="Revision Reason"
                  description="Manage the categories PMs select when creating a resource budget revision. Enables portfolio-level reporting on why budgets change."
                  defaultOpen={false}
                >
                  <RevisionReasonCategoryManager
                    categories={allCategories}
                    onToggle={toggleCategory}
                    onReorder={reorderCategories}
                    onDelete={deleteCategory}
                    onAddClick={() => setAddCategoryDialogOpen(true)}
                  />
                </AccordionSection>
              )}

              <AddRevisionCategoryDialog
                open={addCategoryDialogOpen}
                onOpenChange={setAddCategoryDialogOpen}
                existingLabels={allCategories.map((c) => c.label)}
                onAdd={(label) => {
                  addCategory(label);
                  setAddCategoryDialogOpen(false);
                }}
              />

            </div>
          </TabsContent>

          {/* ── Billing Tab (Feature 9 — Invoice Numbering) ─────────────────── */}
          <TabsContent value="billing" className="mt-0">
            <div className="space-y-4">

              {/* ── Invoice Numbering ────────────────────────────────────────── */}
              <AccordionSection
                icon={<Receipt className="h-5 w-5 text-primary" />}
                title="Invoice Numbering"
                description="Control how invoice numbers are assigned when generating invoices."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Left card — Auto-generate invoice numbers */}
                  <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold">Auto-generate invoice numbers</p>
                      <Checkbox
                        id="auto-numbering-toggle"
                        checked={billingInvoiceSettings.autoNumberingEnabled}
                        onCheckedChange={(checked) => updateAutoNumbering(checked === true)}
                        className="shrink-0"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {billingInvoiceSettings.autoNumberingEnabled
                        ? 'Invoice numbers are assigned automatically by Access Financials when an invoice is posted. No manual entry is required.'
                        : 'Invoice numbers must be entered manually when generating an invoice. The number is validated against Access Financials before posting to prevent duplicates.'}
                    </p>
                  </div>

                  {/* Right card — Last invoice number used */}
                  <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold">Last invoice number used</p>
                      <span className="text-sm font-mono font-medium bg-muted px-3 py-1 rounded-md shrink-0">
                        {billingInvoiceSettings.lastInvoiceNumber}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {billingInvoiceSettings.autoNumberingEnabled
                        ? 'Access Financials manages the number sequence automatically.'
                        : 'Shown as a helper hint in the Invoice Generation Wizard when a manual number is required.'}
                    </p>
                  </div>

                </div>
              </AccordionSection>

              {/* ── Aged WIP Thresholds ──────────────────────────────────────── */}
              {/* PS-11, PS-12, PS-13 — inputs wired to useWipThresholds hook.    */}
              {/* Values are persisted to localStorage until PS-BE-1/BE-2 land.   */}
              <AccordionSection
                icon={<Clock className="h-5 w-5 text-primary" />}
                title="Aged WIP Thresholds"
                description="Define when unbilled work-in-progress is considered aged. These thresholds drive alerts on the WIP report, Finance Dashboard, and individual project overviews."
                defaultOpen={false}
              >
                <div className="flex flex-col gap-4">

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* Card — Aged WIP days threshold */}
                    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                      <p className="text-sm font-semibold">Aged WIP days threshold</p>
                      <p className="text-xs text-muted-foreground">
                        Number of days unbilled WIP must be outstanding before it is flagged as aged. Used in the WIP Report checklist and Finance Dashboard alerts.
                      </p>
                      <div className="flex items-center justify-end gap-2 mt-auto pt-2">
                        <Input
                          id="wip-days-threshold"
                          type="number"
                          min={1}
                          max={365}
                          value={wipThresholds.agedWipDaysThreshold}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v > 0) updateDaysThreshold(v);
                          }}
                          className="w-24 text-right"
                        />
                        <span className="text-sm text-muted-foreground w-10">days</span>
                      </div>
                    </div>

                    {/* Card — WIP amount warning threshold */}
                    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                      <p className="text-sm font-semibold">WIP amount — warning threshold</p>
                      <p className="text-xs text-muted-foreground">
                        WIP balance (£) at which a project's WIP status turns amber on the project overview. Below this value the WIP is considered within normal range.
                      </p>
                      <div className="flex items-center justify-end gap-2 mt-auto pt-2">
                        <span className="text-sm text-muted-foreground">£</span>
                        <Input
                          id="wip-amount-warning"
                          type="number"
                          min={0}
                          step={1000}
                          value={wipThresholds.agedWipAmountWarning}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 0) updateAmountWarning(v);
                          }}
                          className="w-32 text-right"
                        />
                      </div>
                    </div>

                    {/* Card — WIP amount alert threshold */}
                    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
                      <p className="text-sm font-semibold">WIP amount — alert threshold</p>
                      <p className="text-xs text-muted-foreground">
                        WIP balance (£) at which a project's WIP status turns red on the project overview. Should be higher than the warning threshold above.
                      </p>
                      <div className="flex items-center justify-end gap-2 mt-auto pt-2">
                        <span className="text-sm text-muted-foreground">£</span>
                        <Input
                          id="wip-amount-alert"
                          type="number"
                          min={0}
                          step={1000}
                          value={wipThresholds.agedWipAmountAlert}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 0) updateAmountAlert(v);
                          }}
                          className="w-32 text-right"
                        />
                      </div>
                    </div>

                  </div>

                  {wipThresholds.agedWipAmountAlert <= wipThresholds.agedWipAmountWarning && (
                    <Alert variant="destructive" className="py-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        The alert threshold must be greater than the warning threshold.
                      </AlertDescription>
                    </Alert>
                  )}

                </div>
              </AccordionSection>

              {/* ── Write-Off Reasons ────────────────────────────────────────── */}
              <AccordionSection
                icon={<XCircle className="h-5 w-5 text-primary" />}
                title="Write-Off Reasons"
                description="Manage the list of reasons available when writing off WIP items, and whether a reason is mandatory."
                defaultOpen={false}
              >
                <WriteOffReasonsSettings bare />
              </AccordionSection>

            </div>
          </TabsContent>

          {/* ── Reference Data Tab ──────────────────────────────────────────── */}
          <TabsContent value="reference-data" className="mt-0" data-tour="settings-project-statuses">
            <div className="space-y-4">

              {/* ── Section 1: Status Labels ──────────────────────────────── */}
              <AccordionSection
                icon={<Pencil className="h-5 w-5 text-primary" />}
                title="Status Labels"
                description="Customise the status labels used across all projects."
              >
                <ProjectStatusLabels ref={statusLabelsRef} onDirtyChange={setHasStatusLabelChanges} />
              </AccordionSection>


            </div>
          </TabsContent>
          <AlertDialog open={refDataShowDeleteDialog} onOpenChange={setRefDataShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {refDataSelectedCategory?.usageCount === 0 ? 'Delete Category' : 'Archive Category'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {refDataSelectedCategory?.usageCount === 0 ? (
                    <>Are you sure you want to delete <strong>{refDataSelectedCategory?.name}</strong>? This action cannot be undone.</>
                  ) : (
                    <><strong>{refDataSelectedCategory?.name}</strong> is used in {refDataSelectedCategory?.usageCount} project(s). You cannot delete it, but you can archive it to prevent use in new projects.</>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={refDataIsDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmRefDataDelete} disabled={refDataIsDeleting}>
                  {refDataIsDeleting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{refDataSelectedCategory?.usageCount === 0 ? 'Deleting…' : 'Archiving…'}</>
                  ) : refDataSelectedCategory?.usageCount === 0 ? 'Delete' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Global Budget Categories — Add / Edit dialog */}
          <CategoryFormDialog
            open={refDataShowFormDialog}
            onOpenChange={setRefDataShowFormDialog}
            category={refDataEditingCategory}
            onSave={handleRefDataSaveCategory}
            existingCategories={refDataCategories}
          />

          {/* Global Budget Categories — Projects sheet */}
          <CategoryProjectsSheet
            open={refDataShowProjectsSheet}
            onOpenChange={setRefDataShowProjectsSheet}
            category={refDataProjectsSheetCategory}
          />

          {/* Health & Metrics Tab */}
          <TabsContent value="thresholds" className="mt-0" data-tour="settings-project-health">
            <div className="space-y-4">

              {/* ── COLOUR LEGEND — tab level, applies to all health sections ── */}
              <div className="flex flex-wrap gap-6 px-1">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-success-400" />
                  <span className="text-sm font-medium text-success-700 dark:text-success-400">Green</span>
                  <span className="text-sm text-muted-foreground">— all dimensions healthy</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-warning-400" />
                  <span className="text-sm font-medium text-warning-700 dark:text-warning-400">Amber</span>
                  <span className="text-sm text-muted-foreground">— at least one dimension at amber</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-error-400" />
                  <span className="text-sm font-medium text-error-700 dark:text-error-400">Red</span>
                  <span className="text-sm text-muted-foreground">— at least one dimension at red</span>
                </div>
              </div>

              {/* ── Sub-group 1: Financial Health ───────────────────────────────── */}
              <AccordionSection
                icon={<TrendingUp className="h-5 w-5 text-primary" />}
                title="Financial Health"
                description="Thresholds for cost budget consumption, gross margin, realisation rate, and WIP/billing — the financial dimensions of project health."
              >
                <div className="space-y-4">

                  {/* ── DIMENSION CARDS ── */}
                  <div className="grid grid-cols-1 gap-4">

                    {/* ── CARD 1: COST BUDGET ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Cost Budget</h3>
                        <span className="text-xs text-muted-foreground">— % of total cost budget consumed</span>
                      </div>

                      {/* Three-column input row */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Green */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">Below this % budget consumed — on track.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={0} max={budgetThresholds.costAmberThreshold - 1} step={1}
                              value={budgetThresholds.costAmberThreshold - 1}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything below amber</p>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this % consumed — caution.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={50} max={budgetThresholds.costRedThreshold - 1} step={1}
                              value={budgetThresholds.costAmberThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 50 && val < budgetThresholds.costRedThreshold) {
                                  updateBudgetThreshold('costAmberThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Red */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this % consumed — over budget.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={budgetThresholds.costAmberThreshold + 1} max={300} step={1}
                              value={budgetThresholds.costRedThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > budgetThresholds.costAmberThreshold) {
                                  updateBudgetThreshold('costRedThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                      </div>
                      {/* Live preview bar — normal direction (high is bad) */}
                      {(() => {
                        const amber = budgetThresholds.costAmberThreshold;
                        const red = budgetThresholds.costRedThreshold;
                        const max = Math.round(red * 1.1);
                        const greenPct = (amber / max) * 100;
                        const amberPct = ((red - amber) / max) * 100;
                        const redPct = 100 - greenPct - amberPct;
                        return (
                          <div className="space-y-1.5 pt-1">
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-warning-600 dark:text-warning-400">{amber}%</span>
                              <span className="text-error-600 dark:text-error-400">{red}%</span>
                              <span>{max}%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── CARD 2: GROSS MARGIN ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-muted-foreground" />
                          <h3 className="text-sm font-semibold">Gross Margin</h3>
                          <span className="text-xs text-muted-foreground">— actual GM% thresholds</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="margin-enabled-toggle"
                            checked={budgetThresholds.marginEnabled}
                            onCheckedChange={(checked) => updateBudgetThreshold('marginEnabled', checked === true)}
                          />
                          <Label htmlFor="margin-enabled-toggle" className="text-xs text-muted-foreground cursor-pointer">
                            Include in health score
                          </Label>
                        </div>
                      </div>

                      {!budgetThresholds.marginEnabled && (
                        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                          Gross Margin is excluded from the health rollup. Enable once nominal code mapping is configured on the Cost Category Mapping settings page.
                        </p>
                      )}

                      {/* Three-column input row */}
                      <div className={`grid grid-cols-3 gap-4 ${!budgetThresholds.marginEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        {/* Red — reversed: low is bad */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">GM% below this value — critical.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={0} max={budgetThresholds.marginAmberThreshold - 1} step={1}
                              value={budgetThresholds.marginRedThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0 && val < budgetThresholds.marginAmberThreshold) {
                                  updateBudgetThreshold('marginRedThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">GM% below this value — caution.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={budgetThresholds.marginRedThreshold + 1} max={100} step={1}
                              value={budgetThresholds.marginAmberThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > budgetThresholds.marginRedThreshold && val <= 100) {
                                  updateBudgetThreshold('marginAmberThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Green — auto */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this GM% — on track.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              value={budgetThresholds.marginAmberThreshold}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything above amber</p>
                        </div>
                      </div>
                      {/* Live preview bar — reversed direction (low is bad) */}
                      {(() => {
                        const red = budgetThresholds.marginRedThreshold;
                        const amber = budgetThresholds.marginAmberThreshold;
                        const max = Math.round(Math.max(amber * 1.5, 50));
                        const redPct = (red / max) * 100;
                        const amberPct = ((amber - red) / max) * 100;
                        const greenPct = 100 - redPct - amberPct;
                        return (
                          <div className={`space-y-1.5 pt-1 ${!budgetThresholds.marginEnabled ? 'opacity-40' : ''}`}>
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-error-600 dark:text-error-400">{red}%</span>
                              <span className="text-warning-600 dark:text-warning-400">{amber}%</span>
                              <span>{max}%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── CARD 3: REALISATION RATE ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                          <h3 className="text-sm font-semibold">Realisation Rate</h3>
                          <span className="text-xs text-muted-foreground">— actual billed vs potential billable value</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="realisation-enabled-toggle"
                            checked={budgetThresholds.realisationEnabled}
                            onCheckedChange={(checked) => updateBudgetThreshold('realisationEnabled', checked === true)}
                          />
                          <Label htmlFor="realisation-enabled-toggle" className="text-xs text-muted-foreground cursor-pointer">
                            Include in health score
                          </Label>
                        </div>
                      </div>

                      {!budgetThresholds.realisationEnabled && (
                        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                          Realisation Rate is excluded from the health rollup. Enable if your firm uses billable rate cards on timesheet entries.
                        </p>
                      )}

                      {/* Three-column input row */}
                      <div className={`grid grid-cols-3 gap-4 ${!budgetThresholds.realisationEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        {/* Red — reversed: low is bad */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">Realisation below this % — critical.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={0} max={budgetThresholds.realisationAmberThreshold - 1} step={1}
                              value={budgetThresholds.realisationRedThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0 && val < budgetThresholds.realisationAmberThreshold) {
                                  updateBudgetThreshold('realisationRedThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">Realisation below this % — caution.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={budgetThresholds.realisationRedThreshold + 1} max={100} step={1}
                              value={budgetThresholds.realisationAmberThreshold}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > budgetThresholds.realisationRedThreshold && val <= 100) {
                                  updateBudgetThreshold('realisationAmberThreshold', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Green — auto */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this realisation % — on track.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              value={budgetThresholds.realisationAmberThreshold}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything above amber</p>
                        </div>
                      </div>
                      {/* Live preview bar — reversed direction (low is bad) */}
                      {(() => {
                        const red = budgetThresholds.realisationRedThreshold;
                        const amber = budgetThresholds.realisationAmberThreshold;
                        const max = 100;
                        const redPct = (red / max) * 100;
                        const amberPct = ((amber - red) / max) * 100;
                        const greenPct = 100 - redPct - amberPct;
                        return (
                          <div className={`space-y-1.5 pt-1 ${!budgetThresholds.realisationEnabled ? 'opacity-40' : ''}`}>
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-error-600 dark:text-error-400">{red}%</span>
                              <span className="text-warning-600 dark:text-warning-400">{amber}%</span>
                              <span>100%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── CARD 4: WIP / BILLING ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">WIP / Billing</h3>
                        <span className="text-xs text-muted-foreground">— unbilled WIP amount thresholds</span>
                      </div>

                      {/* Mode toggle */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium">WIP threshold mode</p>
                        <p className="text-xs text-muted-foreground">
                          <strong>Absolute</strong> compares the raw WIP balance against fixed £ amounts — the same threshold applies regardless of project size.<br />
                          <strong>Relative</strong> compares WIP as a percentage of the project's budgeted revenue — better for firms with projects of varying sizes.
                        </p>
                        <ToggleGroup
                          type="single"
                          value={budgetThresholds.wipThresholdMode}
                          onValueChange={(val) => {
                            if (val === 'absolute' || val === 'percent') {
                              updateBudgetThreshold('wipThresholdMode', val);
                            }
                          }}
                          className="justify-start"
                        >
                          <ToggleGroupItem value="absolute" className="text-xs px-3">Absolute (£)</ToggleGroupItem>
                          <ToggleGroupItem value="percent" className="text-xs px-3">Relative (% of budget)</ToggleGroupItem>
                        </ToggleGroup>
                      </div>

                      {/* Absolute mode */}
                      {budgetThresholds.wipThresholdMode === 'absolute' && (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="wip-amber-threshold" className="text-sm font-medium">Amber threshold</Label>
                            <p className="text-xs text-muted-foreground">When WIP balance exceeds this amount, turns amber.</p>
                            <div className="relative w-full">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                              <Input
                                id="wip-amber-threshold"
                                type="number"
                                min={0}
                                step={1000}
                                value={budgetThresholds.wipAmberThreshold}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (!isNaN(val) && val >= 0 && val < budgetThresholds.wipRedThreshold) {
                                    updateBudgetThreshold('wipAmberThreshold', val);
                                  }
                                }}
                                className="pl-7"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wip-red-threshold" className="text-sm font-medium">Red threshold</Label>
                            <p className="text-xs text-muted-foreground">When WIP balance exceeds this amount, turns red.</p>
                            <div className="relative w-full">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                              <Input
                                id="wip-red-threshold"
                                type="number"
                                min={0}
                                step={1000}
                                value={budgetThresholds.wipRedThreshold}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (!isNaN(val) && val > budgetThresholds.wipAmberThreshold) {
                                    updateBudgetThreshold('wipRedThreshold', val);
                                  }
                                }}
                                className="pl-7"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Relative / percent mode */}
                      {budgetThresholds.wipThresholdMode === 'percent' && (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">Amber threshold</p>
                              <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[4.5rem] justify-center">
                                {budgetThresholds.wipAmberPct}%
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">When WIP exceeds this % of budgeted revenue, turns amber.</p>
                            <Slider
                              value={[budgetThresholds.wipAmberPct]}
                              onValueChange={([val]) => {
                                if (val < budgetThresholds.wipRedPct - 1) {
                                  updateBudgetThreshold('wipAmberPct', val);
                                }
                              }}
                              min={1} max={49} step={1} className="w-full"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground"><span>1%</span><span>49%</span></div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">Red threshold</p>
                              <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[4.5rem] justify-center">
                                {budgetThresholds.wipRedPct}%
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">When WIP exceeds this % of budgeted revenue, turns red.</p>
                            <Slider
                              value={[budgetThresholds.wipRedPct]}
                              onValueChange={([val]) => {
                                if (val > budgetThresholds.wipAmberPct + 1) {
                                  updateBudgetThreshold('wipRedPct', val);
                                }
                              }}
                              min={2} max={75} step={1} className="w-full"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground"><span>2%</span><span>75%</span></div>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        resetBudgetThresholds();
                        toast.info('Financial health thresholds reset to defaults. Save to apply.');
                      }}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>
                  </div>

                </div>
              </AccordionSection>

              {/* ── Sub-group 2: Schedule Health ────────────────────────────────── */}
              <AccordionSection
                icon={<CalendarDays className="h-5 w-5 text-primary" />}
                title="Schedule Health"
                description="Thresholds for schedule gap (completion vs time elapsed) and forecast end date overruns — covering both percentage-based and day-count signals."
                defaultOpen={false}
              >
                <div className="space-y-4">

                  <div className="grid grid-cols-1 gap-4">

                    {/* ── CARD 1: SCHEDULE GAP ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Schedule Gap</h3>
                        <span className="text-xs text-muted-foreground">— completion % vs time elapsed % gap</span>
                      </div>

                      {/* Three-column input row */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Green */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">Gap below this — schedule on track.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              value={budgetThresholds.scheduleAmberGap - 1}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything below amber</p>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">Gap at or above this — falling behind.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={1} max={budgetThresholds.scheduleRedGap - 1} step={1}
                              value={budgetThresholds.scheduleAmberGap}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 1 && val < budgetThresholds.scheduleRedGap) {
                                  updateBudgetThreshold('scheduleAmberGap', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Red */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">Gap at or above this — critical delay.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={budgetThresholds.scheduleAmberGap + 1} max={100} step={1}
                              value={budgetThresholds.scheduleRedGap}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > budgetThresholds.scheduleAmberGap) {
                                  updateBudgetThreshold('scheduleRedGap', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                      </div>
                      {/* Live preview bar — normal direction (high gap is bad) */}
                      {(() => {
                        const amber = budgetThresholds.scheduleAmberGap;
                        const red = budgetThresholds.scheduleRedGap;
                        const max = Math.round(red * 1.1);
                        const greenPct = (amber / max) * 100;
                        const amberPct = ((red - amber) / max) * 100;
                        const redPct = 100 - greenPct - amberPct;
                        return (
                          <div className="space-y-1.5 pt-1">
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-warning-600 dark:text-warning-400">{amber}%</span>
                              <span className="text-error-600 dark:text-error-400">{red}%</span>
                              <span>{max}%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── CARD 2: FORECAST SCHEDULE OVERRUN ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Forecast Schedule Overrun</h3>
                        <span className="text-xs text-muted-foreground">— days forecast exceeds planned end date</span>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="forecast-amber-days" className="text-sm font-medium">Amber threshold (days)</Label>
                          <p className="text-xs text-muted-foreground">When forecast end date exceeds planned end date by this many days, turns amber.</p>
                          <Input
                            id="forecast-amber-days"
                            type="number"
                            min={0}
                            max={budgetThresholds.forecastScheduleRedDays - 1}
                            step={1}
                            value={budgetThresholds.forecastScheduleAmberDays}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val >= 0 && val < budgetThresholds.forecastScheduleRedDays) {
                                updateBudgetThreshold('forecastScheduleAmberDays', val);
                              }
                            }}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="forecast-red-days" className="text-sm font-medium">Red threshold (days)</Label>
                          <p className="text-xs text-muted-foreground">When forecast end date exceeds planned end date by this many days, turns red.</p>
                          <Input
                            id="forecast-red-days"
                            type="number"
                            min={budgetThresholds.forecastScheduleAmberDays + 1}
                            step={1}
                            value={budgetThresholds.forecastScheduleRedDays}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val > budgetThresholds.forecastScheduleAmberDays) {
                                updateBudgetThreshold('forecastScheduleRedDays', val);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        resetBudgetThresholds();
                        toast.info('Schedule health thresholds reset to defaults. Save to apply.');
                      }}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>
                  </div>

                </div>
              </AccordionSection>

              {/* ── Sub-group 3: Resource Health ────────────────────────────────── */}
              <AccordionSection
                icon={<Users className="h-5 w-5 text-primary" />}
                title="Resource Health"
                description="Thresholds for resource coverage — hours-based amber/red signals and the fully/partially resourced badge boundaries."
              >
                <div className="space-y-4">

                  <div className="grid grid-cols-1 gap-4">

                    {/* ── CARD 1: HOURS COVERAGE ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Hours Coverage</h3>
                        <span className="text-xs text-muted-foreground">— actual hours vs budget hours consumed %</span>
                      </div>

                      {/* Three-column input row */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Green */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">Below this % hours consumed — on track.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              value={budgetThresholds.hoursAmberGap - 1}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything below amber</p>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this % hours consumed — caution.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={50} max={budgetThresholds.hoursRedGap - 1} step={1}
                              value={budgetThresholds.hoursAmberGap}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 50 && val < budgetThresholds.hoursRedGap) {
                                  updateBudgetThreshold('hoursAmberGap', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Red */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this % hours consumed — over budget.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={budgetThresholds.hoursAmberGap + 1} max={300} step={1}
                              value={budgetThresholds.hoursRedGap}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > budgetThresholds.hoursAmberGap) {
                                  updateBudgetThreshold('hoursRedGap', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                      </div>
                      {/* Live preview bar — normal direction (high consumption is bad) */}
                      {(() => {
                        const amber = budgetThresholds.hoursAmberGap;
                        const red = budgetThresholds.hoursRedGap;
                        const max = Math.round(red * 1.1);
                        const greenPct = (amber / max) * 100;
                        const amberPct = ((red - amber) / max) * 100;
                        const redPct = 100 - greenPct - amberPct;
                        return (
                          <div className="space-y-1.5 pt-1">
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-warning-600 dark:text-warning-400">{amber}%</span>
                              <span className="text-error-600 dark:text-error-400">{red}%</span>
                              <span>{max}%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── CARD 2: RESOURCE COVERAGE BADGES ── */}
                    <div className="rounded-lg border border-border p-5 space-y-5">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Resource Coverage Badges</h3>
                        <span className="text-xs text-muted-foreground">— fully and partially resourced thresholds</span>
                      </div>

                      {/* Three-column input row — reversed (low coverage = bad) */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Red */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-error-700 dark:text-error-400">Red threshold</p>
                          <p className="text-xs text-muted-foreground">Below this coverage % — shows a red "Unresourced" badge.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              value={thresholds.partiallyResourcedMin}
                              readOnly
                              className="h-8 text-sm opacity-60 cursor-not-allowed"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Auto: anything below amber</p>
                        </div>
                        {/* Amber */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-warning-700 dark:text-warning-400">Amber threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this coverage % — shows a "Partially Resourced" badge.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={0} max={thresholds.fullyResourcedMin - 1} step={1}
                              value={thresholds.partiallyResourcedMin}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0 && val < thresholds.fullyResourcedMin) {
                                  updateThreshold('partiallyResourcedMin', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                        {/* Green */}
                        <div className="rounded-md border border-border p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-success-700 dark:text-success-400">Green threshold</p>
                          <p className="text-xs text-muted-foreground">At or above this coverage % — shows a green "Fully Resourced" badge.</p>
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number" min={thresholds.partiallyResourcedMin + 1} max={100} step={1}
                              value={thresholds.fullyResourcedMin}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val > thresholds.partiallyResourcedMin && val <= 100) {
                                  updateThreshold('fullyResourcedMin', val);
                                }
                              }}
                              className="h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground shrink-0">%</span>
                          </div>
                        </div>
                      </div>
                      {/* Live preview bar — reversed direction (low coverage = bad) */}
                      {(() => {
                        const amber = thresholds.partiallyResourcedMin;
                        const green = thresholds.fullyResourcedMin;
                        const redPct = (amber / 100) * 100;
                        const amberPct = ((green - amber) / 100) * 100;
                        const greenPct = 100 - redPct - amberPct;
                        return (
                          <div className="space-y-1.5 pt-1">
                            <div className="flex h-4 w-full overflow-hidden rounded-full">
                              <div style={{ width: `${redPct}%` }} className="bg-error-400 transition-all duration-200" />
                              <div style={{ width: `${amberPct}%` }} className="bg-warning-400 transition-all duration-200" />
                              <div style={{ width: `${greenPct}%` }} className="bg-success-400 transition-all duration-200" />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>0%</span>
                              <span className="text-error-600 dark:text-error-400">{amber}%</span>
                              <span className="text-success-600 dark:text-success-400">{green}%</span>
                              <span>100%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        resetToDefaults();
                        resetBudgetThresholds();
                        toast.info('Resource health thresholds reset to defaults. Save to apply.');
                      }}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>
                  </div>

                </div>
              </AccordionSection>

            </div>
          </TabsContent>

          {/* Key People Tab */}
          <TabsContent value="key-people" className="mt-0">
            <div className="space-y-4">
              <AccordionSection
                icon={<Users className="h-5 w-5 text-primary" />}
                title="Key People Role Configuration"
                description="Control which accountability roles are visible on projects, whether they are required, and customise their display labels."
              >
                <div className="space-y-0 divide-y">
                  {/* Column headings */}
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center pb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <span>Role</span>
                    <span className="w-20 text-center whitespace-nowrap">Enabled</span>
                    <span className="w-20 text-center whitespace-nowrap">Required</span>
                    <span className="w-44">Display Label</span>
                  </div>

                  {roleConfigs.map((config) => {
                    const defaultLabel = PROJECT_ROLE_LABELS[config.role];
                    // TODO [ENGINEERING — Devin]: The warning banner count below is hardcoded to 0.
                    // Replace with a value from GET /api/projects/role-assignment-counts
                    // which returns Record<ProjectRoleType, number>.
                    // Show the banner only when count > 0 and enabled is being toggled off.
                    const activeAssignmentCount: number = 0; // TODO [ENGINEERING — Devin]: replace with API value
                    return (
                      <div key={config.role} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center py-3">
                        {/* Role name + warning */}
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{defaultLabel}</p>
                          {!config.enabled && activeAssignmentCount > 0 && (
                            <p className="text-xs text-warning-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {activeAssignmentCount} project{activeAssignmentCount !== 1 ? 's have' : ' has'} {defaultLabel} assignments — hidden but not deleted.
                            </p>
                          )}
                        </div>

                        {/* Enabled checkbox */}
                        <div className="w-20 flex justify-center">
                          <Checkbox
                            checked={config.enabled}
                            onCheckedChange={(checked) => updateRoleConfig(config.role, { enabled: checked === true })}
                            disabled={config.role === 'project-manager'}
                            aria-label={`${defaultLabel} enabled`}
                          />
                        </div>

                        {/* Required checkbox */}
                        <div className="w-20 flex justify-center">
                          <Checkbox
                            checked={config.required}
                            onCheckedChange={(checked) => updateRoleConfig(config.role, { required: checked === true })}
                            disabled={!config.enabled}
                            aria-label={`${defaultLabel} required`}
                          />
                        </div>

                        {/* Display label override */}
                        <div className="w-44">
                          <Input
                            value={config.displayLabel ?? ''}
                            onChange={(e) =>
                              updateRoleConfig(config.role, {
                                displayLabel: e.target.value || undefined,
                              })
                            }
                            placeholder={defaultLabel}
                            disabled={!config.enabled}
                            className="h-8 text-sm"
                            aria-label={`${defaultLabel} display label`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionSection>

              {/* Info note */}
              <Alert variant="info">
                <AlertDescription>
                  <strong>Project Manager is always enabled and required.</strong> All other roles are optional.
                  Disabling a role hides it from all projects but preserves existing assignments — re-enabling it restores visibility.
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>

          {/* Project Fields Tab — merged Content Fields + Custom Fields */}
          <TabsContent value="project-fields" className="mt-0">
            <div className="space-y-4">

              {/* ── Sub-section 1: Content Sections ──────────────────────────── */}
              <AccordionSection
                icon={<LayoutList className="h-5 w-5 text-primary" />}
                title="Content Sections"
                description="Control which narrative content cards appear on the Project Overview tab. Hidden cards preserve their content."
              >
                <div className="space-y-3">
                  {/* Objectives row */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="content-objectives"
                      checked={contentSettings.showObjectives}
                      onCheckedChange={(checked) => updateContentSetting('showObjectives', checked === true)}
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="content-objectives" className="text-sm font-medium cursor-pointer">
                        Project Objectives
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Why the project exists — strategic goals, success criteria, and what "done well" looks like.
                      </p>
                    </div>
                  </div>

                  {/* Key Deliverables row */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="content-deliverables"
                      checked={contentSettings.showKeyDeliverables}
                      onCheckedChange={(checked) => updateContentSetting('showKeyDeliverables', checked === true)}
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="content-deliverables" className="text-sm font-medium cursor-pointer">
                        Key Deliverables
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        What the project produces — tangible outputs, artefacts, systems, or services being delivered.
                      </p>
                    </div>
                  </div>

                  {/* Project Notes row */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="content-notes"
                      checked={contentSettings.showNotes}
                      onCheckedChange={(checked) => updateContentSetting('showNotes', checked === true)}
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="content-notes" className="text-sm font-medium cursor-pointer">
                        Project Notes
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Running PM context — decisions made, client nuances, escalation history, and handover information.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info note inside accordion */}
                <div className="mt-4 pt-4 border-t">
                  <Alert variant="info">
                    <AlertDescription>
                      <strong>Content is never deleted when a field is hidden.</strong> All existing Objectives, Deliverables, and Notes are preserved. Re-enabling a field instantly restores visibility across all projects.
                    </AlertDescription>
                  </Alert>
                </div>
              </AccordionSection>

              {/* ── Sub-section 2: Custom Fields ──────────────────────────────── */}
              <AccordionSection
                icon={<SlidersHorizontal className="h-5 w-5 text-primary" />}
                title="Custom Fields"
                description="Define typed custom fields that appear on the Project Attributes card on every project's Overview tab."
                defaultOpen={false}
              >
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Badge variant="secondary" className="text-xs font-normal">
                      {customFieldDefs.filter((d) => d.enabled).length} / {MAX_FIELDS} fields used
                    </Badge>
                  </div>
                  {/* Field list */}
                  {customFieldDefs.length === 0 && !isAddingField ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/25 p-10 text-center text-muted-foreground gap-2">
                      <SlidersHorizontal className="h-8 w-8 opacity-30" />
                      <p className="text-sm font-medium">No custom fields defined yet</p>
                      <p className="text-xs">Add your first field to start capturing project-specific attributes.</p>
                    </div>
                  ) : (
                    <div className="divide-y rounded-lg border">
                      {/* Column headers */}
                      <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center px-4 py-2 bg-muted/40 text-xs font-medium text-muted-foreground">
                        <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>Field</span>
                        <span className="w-12 text-center">Required</span>
                        <span className="w-12 text-center">Enabled</span>
                        <span className="w-16 text-center">Order</span>
                        <span className="w-8" />
                      </div>
                      {[...customFieldDefs]
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((def, idx, arr) => (
                          <div
                            key={def.id}
                            className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center px-4 py-3"
                          >
                            {/* Drag handle placeholder */}
                            <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />

                            {/* Label + type badge + options (all in one line) */}
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">
                              <Input
                                value={def.label}
                                onChange={(e) =>
                                  updateDefinition(def.id, { label: e.target.value })
                                }
                                className="h-7 text-sm w-40 shrink-0"
                                aria-label={`Label for field ${def.label}`}
                              />
                              <Badge variant="outline" className="text-xs font-normal shrink-0">
                                {FIELD_TYPE_LABELS[def.fieldType]}
                              </Badge>
                              {def.fieldType === 'select' && (
                                editingOptionsId === def.id ? (
                                  /* ── Inline options editor ── */
                                  <div className="flex items-center gap-1.5">
                                    <Input
                                      autoFocus
                                      value={editingOptionsValue}
                                      onChange={(e) => setEditingOptionsValue(e.target.value)}
                                      placeholder="Option A, Option B, Option C"
                                      className="h-7 text-xs w-48"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          const opts = editingOptionsValue.split(',').map((o) => o.trim()).filter(Boolean);
                                          updateDefinition(def.id, { options: opts });
                                          setEditingOptionsId(null);
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingOptionsId(null);
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const opts = editingOptionsValue.split(',').map((o) => o.trim()).filter(Boolean);
                                        updateDefinition(def.id, { options: opts });
                                        setEditingOptionsId(null);
                                      }}
                                      className="p-1 rounded text-primary hover:bg-primary/10 transition-colors"
                                      aria-label="Save options"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingOptionsId(null)}
                                      className="p-1 rounded text-muted-foreground hover:bg-muted transition-colors"
                                      aria-label="Cancel editing options"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  /* ── Read display ── */
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingOptionsId(def.id);
                                      setEditingOptionsValue((def.options ?? []).join(', '));
                                    }}
                                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground group"
                                    aria-label={`Edit options for ${def.label}`}
                                  >
                                    <span className="truncate max-w-[160px]">
                                      {def.options && def.options.length > 0
                                        ? def.options.join(', ')
                                        : 'Add options'}
                                    </span>
                                    <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                                  </button>
                                )
                              )}
                            </div>

                            {/* Required checkbox */}
                            <div className="w-12 flex justify-center">
                              <Checkbox
                                checked={def.required}
                                onCheckedChange={(checked) =>
                                  updateDefinition(def.id, { required: checked === true })
                                }
                                disabled={!def.enabled}
                                aria-label={`${def.label} required`}
                              />
                            </div>

                            {/* Enabled checkbox */}
                            <div className="w-12 flex justify-center">
                              <Checkbox
                                checked={def.enabled}
                                onCheckedChange={(checked) =>
                                  updateDefinition(def.id, {
                                    enabled: checked === true,
                                    // If disabling, also clear required
                                    required: checked === true ? def.required : false,
                                  })
                                }
                                aria-label={`${def.label} enabled`}
                              />
                            </div>

                            {/* Move up / down */}
                            <div className="w-16 flex items-center justify-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => handleMoveField(def.id, 'up')}
                                disabled={idx === 0}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move ${def.label} up`}
                              >
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveField(def.id, 'down')}
                                disabled={idx === arr.length - 1}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move ${def.label} down`}
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </div>

                            {/* Delete */}
                            <div className="w-8 flex justify-center">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                    aria-label={`Delete ${def.label}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete "{def.label}"?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently remove the field definition and{' '}
                                      <strong>all stored values across every project</strong>.
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => removeDefinition(def.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete field
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Add field form */}
                  {isAddingField ? (
                    <div className="rounded-lg border border-info-200 bg-info-50/30 p-4 space-y-4">
                      <p className="text-sm font-medium">New field</p>
                      <div className="flex items-end gap-3 flex-wrap">
                        {/* Label */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <Label htmlFor="new-field-label" className="text-xs">
                            Label <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="new-field-label"
                            value={newFieldLabel}
                            onChange={(e) => setNewFieldLabel(e.target.value)}
                            placeholder="e.g. Contract Reference"
                            className="h-8 text-sm"
                            autoFocus
                          />
                        </div>

                        {/* Field type */}
                        <div className="w-36 space-y-1.5">
                          <Label htmlFor="new-field-type" className="text-xs">Field Type</Label>
                          <Select
                            value={newFieldType}
                            onValueChange={(v) => setNewFieldType(v as CustomFieldType)}
                          >
                            <SelectTrigger id="new-field-type" className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(FIELD_TYPE_LABELS) as CustomFieldType[]).map((t) => (
                                <SelectItem key={t} value={t} className="text-sm">
                                  {FIELD_TYPE_LABELS[t]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Options — only for select */}
                        {newFieldType === 'select' && (
                          <div className="w-56 space-y-1.5">
                            <Label htmlFor="new-field-options" className="text-xs">
                              Options <span className="text-muted-foreground">(comma-separated)</span>
                            </Label>
                            <Input
                              id="new-field-options"
                              value={newFieldOptions}
                              onChange={(e) => setNewFieldOptions(e.target.value)}
                              placeholder="Option A, Option B…"
                              className="h-8 text-sm"
                            />
                          </div>
                        )}

                        {/* Required checkbox */}
                        <div className="flex items-center gap-2 pb-1">
                          <Checkbox
                            id="new-field-required"
                            checked={newFieldRequired}
                            onCheckedChange={(checked) => setNewFieldRequired(checked === true)}
                          />
                          <Label htmlFor="new-field-required" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                            Required
                          </Label>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        <Button variant="outline" size="sm" onClick={handleCancelAddField}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddField}
                          disabled={!newFieldLabel.trim()}
                        >
                          Add Field
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingField(true)}
                      disabled={customFieldDefs.length >= MAX_FIELDS}
                      className="gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add field
                      {customFieldDefs.length >= MAX_FIELDS && (
                        <span className="text-muted-foreground ml-1">(limit reached)</span>
                      )}
                    </Button>
                  )}
                </div>
              </AccordionSection>

              {/* Info note */}
              <Alert variant="info">
                <AlertDescription>
                  <strong>Field type cannot be changed once a field has been saved.</strong>{' '}
                  To change a field's type, disable it and create a new field. Disabling a field hides it from all projects but preserves all stored values — re-enabling it restores visibility instantly.
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>

          {/* ── Project List Tab ─────────────────────────────────────── */}
          <TabsContent value="project-list" className="mt-0">
            <ProjectListSettings />
          </TabsContent>

        </Tabs>

        {/* Sticky footer save bar — hidden inside onboarding wizard (Next button saves instead) */}
        {!isWizardMode && <div className="border-t bg-background px-6 py-3 flex items-center justify-end gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            disabled={isSaving || !hasAnyChanges}
            onClick={() => {
              resetProjectCreationChanges();
              handleDiscardKeyPeopleSettings();
              resetContentSettings();
              discardCustomFieldChanges();
              discardBillingInvoiceSettings();
              discardWipThresholds();
              statusLabelsRef.current?.discard();
            }}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSaveAll} disabled={isSaving || !hasAnyChanges}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : showSaveSuccess ? (
              <Check className="h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {showSaveSuccess ? 'Saved' : 'Save'}
          </Button>
        </div>}

      {/* Cascade Target Confirmation Dialog */}
      <AlertDialog open={showTargetConfirmDialog} onOpenChange={setShowTargetConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingTargetCategoryId === null
                ? 'Remove Cascade Target?'
                : localRevisionSettings.cascadeTargetCategoryId
                  ? 'Change Cascade Target?'
                  : 'Set Cascade Target?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {pendingTargetCategoryId === null ? (
                  <p>
                    Removing the target will cause the cascade to update <strong>all</strong> Labour categories when a resource budget revision is saved. This is the legacy behaviour and may cause unexpected results if you have more than one Labour category.
                  </p>
                ) : (() => {
                  const newCat = labourCategories.find(c => c.id === pendingTargetCategoryId);
                  const oldCat = localRevisionSettings.cascadeTargetCategoryId
                    ? labourCategories.find(c => c.id === localRevisionSettings.cascadeTargetCategoryId)
                    : null;
                  return (
                    <>
                      {oldCat && (
                        <>
                          <p>
                            You are changing the cascade target from <strong>{oldCat.name}</strong> to <strong>{newCat?.name}</strong>.
                            <strong> {oldCat.name}</strong> will be unlocked and can be archived or deleted again.
                          </p>
                          {oldCat.usageCount > 0 && (
                            <p className="text-warning-700 dark:text-warning-300">
                              <strong>Warning:</strong> <strong>{oldCat.name}</strong> has already received cascaded budget data across {oldCat.usageCount} project(s). Changing the target will not retrospectively update those projects — only future resource budget cascades will target the new category.
                            </p>
                          )}
                        </>
                      )}
                      <p>
                        <strong>{newCat?.name}</strong> will become the sole target for resource budget cascading.
                        It will be locked and cannot be archived or deleted while it is the target.
                      </p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingTargetCategoryId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setLocalRevisionSettings(prev => ({ ...prev, cascadeTargetCategoryId: pendingTargetCategoryId }));
              setHasRevisionChanges(true);
              setShowTargetConfirmDialog(false);
              setPendingTargetCategoryId(null);
            }}>
              {pendingTargetCategoryId === null ? 'Remove Target' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Labour Category Dialog (from empty state) */}
      <CategoryFormDialog
        open={showAddLabourDialog}
        onOpenChange={setShowAddLabourDialog}
        category={null}
        initialValues={{
          name: 'Labour',
          type: 'labour',
          lockType: true,
        }}
        onSave={async (data) => {
          const newCategory = await createGlobalCategory({
            name: data.name,
            code: data.code,
            type: data.type,
            description: data.description,
            isDefault: data.isDefault,
          });
          toast.success(`Category "${data.name}" created successfully`);
          await loadLabourCategories();
          setCategoryRefreshKey(prev => prev + 1);
          // Auto-select the newly created category as the cascade target
          if (newCategory?.id) {
            setLocalRevisionSettings(prev => ({ ...prev, cascadeTargetCategoryId: newCategory.id }));
            setHasRevisionChanges(true);
            toast.info(`"${data.name}" set as the cascade target. Save settings to apply.`);
          }
        }}
        existingCategories={labourCategories}
      />
    </div>
  );

  if (isWizardMode) {
    return content;
  }

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </div>
    </SettingsLayout>
  );
}
